import React, { useMemo } from "react";
import { Alert } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import { useBayHireOperationsQueue } from "@features/bay-hire/hooks/useBayHireOperationsQueue";
import type { BayHireQueueRow } from "@features/bay-hire/types";
import {
  bayHirePaymentTypeLabel,
  bayHirePreferenceLabel,
} from "@features/bay-hire/utils/bayHireLabels";
import { formatBayHireDateTime } from "@features/bay-hire/utils/formatBayHireDate";
import { getBayHireStatusBadgeType } from "@features/bay-hire/utils/statusBadge";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

/**
 * DFT-0009 Operations queue view (AC4, AC47).
 *
 * DFT-0027 (Slice B2): the per-row lifecycle actions (Claim/Unclaim/Pre-approve/
 * Reject/Add to Waiting List/Verify Payment/Reject Payment/Cancel) that DFT-0034
 * placed on the row kebab have moved to the routed Request Detail page
 * (BayHireRequestDetail + BayHireFlowButtons). Rows now navigate to that detail
 * route (canPreview + navigationLinkBase) where Operations users act on the
 * request. This component is therefore a read-only, navigable queue.
 *
 * DFT-0027 (Slice E): this is now the "Ongoing" tab of BayHireOperationsTabs -
 * the page chrome (PageLayout + Subheader) and the old "View Waiting List"
 * cross-navigation button moved to that container. Columns follow the Figma
 * p13 set (identical to the vendor list, useBayHireRequestList), and the only
 * row-kebab entry is "View details" (the lifecycle actions stay on the detail
 * header).
 */
const BayHireOperationsQueue: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    rows,
    isLoading,
    isError,
    page,
    pageSize,
    totalCount,
    onPageChange,
    filters,
    availableFields,
    handleApplyFilters,
    handleClearFilters,
  } = useBayHireOperationsQueue("REQUESTED");

  const tableRows = useMemo(
    () =>
      rows.map((row: BayHireQueueRow) => ({
        id: row.request.id,
        requestId: row.request.id,
        // FE-17/27: the visible "ID" column reads this, not `id` (a UUID kept only for row
        // navigation - canPreview reads `id` directly, unaffected by the header's own `field`).
        requestNo: row.request.requestNo ?? "-",
        numberOfBays: row.request.numberOfBays ?? "-",
        responsiblePersonName: row.request.responsiblePersonName,
        bayPreference: row.request.bayPreference,
        paymentType: row.request.paymentType,
        dateFrom: row.request.dateFrom,
        dateTo: row.request.dateTo,
        // DFT-0044 (FE-29): DD/MM/YYYY HH:MM, matching every other Marhub feature's own
        // date+time column (see formatBayHireDate.ts's formatBayHireDateTime).
        createdAt: formatBayHireDateTime(row.request.createdAt),
        status: row.request.status,
        // DFT-0027 (Slice E2): explicit badgeType so columns.tsx renders the
        // colored dot for Bay Hire's raw domain statuses (see statusBadge.ts).
        badgeType: getBayHireStatusBadgeType(row.request.status),
        // DFT-0044 (FE-19): humanized display text, read by columns.tsx's status column.
        statusLabel: t(`bay-hire.status.${row.request.status}`, {
          defaultValue: row.request.status,
        }),
        // DFT-0044: humanized display text for the Request Bay/Payment type columns.
        bayPreferenceLabel: bayHirePreferenceLabel(t, row.request.bayPreference),
        paymentTypeLabel: bayHirePaymentTypeLabel(t, row.request.paymentType),
      })),
    [rows, t],
  );

  // DFT-0027 (Slice E2): Figma p13 column set, mirroring the vendor list
  // (useBayHireRequestList). The previous companyId/taskStatus/owner columns
  // are not part of that set and were dropped; the task snapshot is still used
  // by the detail page's action gating.
  const headers: Header[] = useMemo(
    () => [
      // Megaplan (2026-08-05): explicit content-proportional `flex` on every header - identical
      // values to the vendor list (useBayHireRequestList.tsx) and BayHireWaitingListQueue, since
      // all three share this 9-column shape.
      { field: "requestNo", columnName: t("bay-hire.table.id"), flex: 0.6 },
      // FE-27: baseline order (04_Port_License_details-2.png) puts the company responsible 2nd and
      // the bay count 3rd. Unchanged by batch 2.
      {
        field: "responsiblePersonName",
        columnName: t("bay-hire.table.responsiblePerson"),
        flex: 1.6,
      },
      {
        field: "numberOfBays",
        // Megaplan (2026-08-05): "Bay No" mislabeled the bay COUNT - renamed to "No of bays",
        // shared key with useBayHireRequestList.tsx's own column.
        columnName: t("bay-hire.table.bayNo"),
        flex: 0.6,
      },
      {
        field: "bayPreferenceLabel",
        columnName: t("bay-hire.table.requestBay"),
        flex: 0.9,
      },
      {
        field: "paymentTypeLabel",
        columnName: t("bay-hire.table.paymentType"),
        flex: 0.8,
      },
      {
        field: "dateFrom",
        columnName: t("bay-hire.table.dateFrom"),
        type: "date",
        flex: 0.8,
      },
      {
        field: "dateTo",
        columnName: t("bay-hire.table.dateTo"),
        type: "date",
        flex: 0.8,
      },
      {
        // DFT-0044 (FE-29): NOT type: "dateTime" - see the row mapper above.
        field: "createdAt",
        columnName: t("bay-hire.table.dateSubmitted"),
        flex: 1.0,
      },
      {
        // Field literally "status" (not renderAsChip) so columns.tsx renders
        // the colored-dot Badge from this row's badgeType.
        field: "status",
        columnName: t("bay-hire.table.status"),
        flex: 0.9,
      },
    ],
    [t],
  );

  // DFT-0026: LoadingOverlay (non-blocking) + CustomDataTable's own
  // NoTableEntries (via real title/actionName) replace the previous
  // bespoke CircularProgress/Alert tri-state, aligning with
  // Gatepass/Licensing/Vessel Call/Vessel Management. The error Alert
  // below is intentionally unchanged (see DFT-0026 plan.md Decision 1 -
  // no single comparator error pattern to align to; ClearancesContainer
  // has none, Licenses.tsx's Snackbar never auto-dismisses).
  const queueContent = (
    <div style={{ position: "relative" }}>
      <LoadingOverlay loading={isLoading} testId="bay-hire-operations-loading" />
      <CustomDataTable
        testId="bay-hire-operations-queue-table"
        title={t("bay-hire.operations-queue-title")}
        actionName=""
        headers={headers}
        rows={tableRows}
        page={page}
        pageSize={pageSize}
        // DFT-0027 (Slice E4): Figma's "Rows per page 10" default, matching
        // the vendor list's options.
        pageSizeOptions={[10, 25, 50, 100]}
        totalCount={totalCount}
        // DFT-0015: real offset pagination (previously a no-op beyond
        // page 1, see DFT-0026's flagged prerequisite - now satisfied).
        onPageChange={onPageChange}
        // DFT-0027 (Slice E3): the ⋮ column holds ONLY "View details". The
        // lifecycle actions deliberately stay on the detail header (Slice B2)
        // and must not come back into the row menu.
        actionColumn={{
          actionsDisplay: "menu",
          headerName: t("bay-hire.table.action"),
          actions: [
            {
              label: t("bay-hire.view-details"),
              testId: (row) => `bay-hire-operations-view-details-${row.id}`,
              onClick: (row) => navigate(`/${paths.bayHire.requestDetailBase}/${row.id}`),
            },
          ],
        }}
        // DFT-0027 (Slice B2): a row click navigates to the routed detail page
        // where the Operations lifecycle actions now live. Absolute base since
        // this queue is mounted under /bay-hire/operations, and the detail
        // route is /bay-hire/request-details/:id. The row id === requestId.
        canPreview
        navigationLinkBase={`/${paths.bayHire.requestDetailBase}`}
        // DFT-0027 (Slice G): the Figma p13 Filters panel. hideFilters /
        // displaySubTitle={false} are deliberately NOT set - CustomDataTable
        // gates its whole header block (and with it the Filters button) on
        // `(title || !hideFilters) && displaySubTitle`. hideTitle instead
        // suppresses only the duplicate heading (the page title lives in
        // BayHireOperationsTabs' Subheader) while `title` stays real so
        // CustomDataTable's NoTableEntries empty state still reads correctly.
        filters={filters}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        availableFields={availableFields}
        hideTitle
      />
    </div>
  );

  // DFT-0027 (Slice E1): no PageLayout/Subheader here - BayHireOperationsTabs
  // owns the page chrome for both tabs.
  return (
    <div className="mt-8 px-24" data-testid="bay-hire-operations-queue">
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="bay-hire-operations-load-error">
          {t("bay-hire.operations-queue-load-error")}
        </Alert>
      )}

      {queueContent}
    </div>
  );
};

export default BayHireOperationsQueue;
